import {getAudioDurationInSeconds} from '@remotion/media-utils';
import React from 'react';
import {
	AbsoluteFill,
	Composition,
	interpolate,
	spring,
	staticFile,
	useCurrentFrame,
	useVideoConfig,
} from 'remotion';
import {AudioStory} from './AudioStory/Main';
import {SanhaojiTrailer} from './Sanhaoji/Trailer';
import {TravelDay} from './Yunyou/TravelDay';
import {LightAnatomy} from './ZiAn/Anatomy';
import {NeonRain} from './Neon/NeonRain';
import {audioStorySchema} from './AudioStory/schema';
import {fetchCaptions} from './AudioStory/caption-utils';

const FONT = '-apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", Arial, sans-serif';

const Glow: React.FC<{x: number; y: number; size: number; color: string; opacity: number}> = ({
	x,
	y,
	size,
	color,
	opacity,
}) => (
	<div
		style={{
			position: 'absolute',
			left: x - size / 2,
			top: y - size / 2,
			width: size,
			height: size,
			borderRadius: '50%',
			background: color,
			filter: 'blur(120px)',
			opacity,
		}}
	/>
);

const HelloRemotion: React.FC = () => {
	const frame = useCurrentFrame();
	const {fps, durationInFrames} = useVideoConfig();

	const bgShift = interpolate(frame, [0, durationInFrames], [0, 60]);
	const fadeOut = interpolate(frame, [durationInFrames - 20, durationInFrames], [1, 0], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});

	const letters = 'REMOTION'.split('');

	return (
		<AbsoluteFill
			style={{
				background: `radial-gradient(120% 120% at ${30 + bgShift * 0.4}% ${20 + bgShift * 0.3}%, #1b2a5e 0%, #0b1020 45%, #05060c 100%)`,
				fontFamily: FONT,
				overflow: 'hidden',
			}}
		>
			<Glow x={260 + bgShift * 4} y={220 + Math.sin(frame / 30) * 40} size={620} color="#4361ee" opacity={0.45} />
			<Glow
				x={1680 - bgShift * 3}
				y={860 + Math.cos(frame / 34) * 50}
				size={700}
				color="#9d4edd"
				opacity={0.35}
			/>
			<Glow x={1200} y={180 + Math.sin(frame / 40) * 30} size={480} color="#00b4d8" opacity={0.22} />

			<AbsoluteFill style={{justifyContent: 'center', alignItems: 'center', opacity: fadeOut}}>
				<div style={{display: 'flex', gap: 6}}>
					{letters.map((letter, i) => {
						const progress = spring({
							frame: frame - 10 - i * 3,
							fps,
							config: {damping: 14, stiffness: 120, mass: 0.7},
						});
						const y = interpolate(progress, [0, 1], [90, 0]);
						const opacity = interpolate(progress, [0, 1], [0, 1]);
						return (
							<span
								key={letter + i}
								style={{
									display: 'inline-block',
									transform: `translateY(${y}px) rotate(${(1 - progress) * 6}deg)`,
									opacity,
									fontSize: 176,
									fontWeight: 800,
									letterSpacing: 8,
									color: '#f8f9ff',
									textShadow: '0 0 60px rgba(96, 132, 255, 0.55)',
								}}
							>
								{letter}
							</span>
						);
					})}
				</div>

				<div
					style={{
						marginTop: 28,
						height: 3,
						width: interpolate(spring({frame: frame - 40, fps, config: {damping: 200}}), [0, 1], [0, 520]),
						background: 'linear-gradient(90deg, transparent, #7aa2ff, #c77dff, transparent)',
						borderRadius: 2,
					}}
				/>

				<p
					style={{
						marginTop: 34,
						fontSize: 40,
						letterSpacing: 12,
						fontWeight: 500,
						color: '#c9d4ff',
						opacity: interpolate(spring({frame: frame - 48, fps, config: {damping: 200}}), [0, 1], [0, 1]),
						transform: `translateY(${interpolate(
							spring({frame: frame - 48, fps, config: {damping: 200}}),
							[0, 1],
							[24, 0],
						)}px)`,
					}}
				>
					用 React 写视频
				</p>

				<p
					style={{
						marginTop: 18,
						fontSize: 22,
						letterSpacing: 6,
						color: '#6f7db3',
						opacity: interpolate(spring({frame: frame - 64, fps, config: {damping: 200}}), [0, 1], [0, 1]),
					}}
				>
					REMOTION STUDIO · 60 FPS PREVIEW
				</p>
			</AbsoluteFill>
		</AbsoluteFill>
	);
};

const PulseGrid: React.FC = () => {
	const frame = useCurrentFrame();
	const cols = 9;
	const rows = 9;
	const cells = Array.from({length: cols * rows}, (_, i) => i);

	return (
		<AbsoluteFill
			style={{
				background: 'linear-gradient(160deg, #06070d 0%, #0d1330 55%, #131a3c 100%)',
				fontFamily: FONT,
				justifyContent: 'center',
				alignItems: 'center',
				overflow: 'hidden',
			}}
		>
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: `repeat(${cols}, 84px)`,
					gap: 16,
					position: 'absolute',
					inset: 0,
					alignContent: 'center',
					justifyContent: 'center',
				}}
			>
				{cells.map((i) => {
					const col = i % cols;
					const row = Math.floor(i / cols);
					const delay = (col + row) * 4;
					const pulse = Math.sin((frame - delay) / 10);
					const scale = 1 + Math.max(0, pulse) * 0.32;
					const alpha = 0.08 + Math.max(0, pulse) * 0.45;
					return (
						<div
							key={i}
							style={{
								width: 84,
								height: 84,
								borderRadius: 18,
								border: '1px solid rgba(122, 162, 255, 0.28)',
								background: `rgba(122, 162, 255, ${alpha * 0.35})`,
								boxShadow: `0 0 ${30 * Math.max(0, pulse)}px rgba(122, 162, 255, ${alpha})`,
								transform: `scale(${scale})`,
							}}
						/>
					);
				})}
			</div>

			<div
				style={{
					position: 'relative',
					textAlign: 'center',
					padding: '48px 72px',
					borderRadius: 28,
					background: 'rgba(6, 8, 16, 0.72)',
					border: '1px solid rgba(122, 162, 255, 0.25)',
					backdropFilter: 'blur(16px)',
					boxShadow: '0 20px 80px rgba(0, 0, 0, 0.6)',
				}}
			>
				<div style={{fontSize: 68, fontWeight: 800, letterSpacing: 14, color: '#f4f7ff'}}>STUDIO</div>
				<div style={{marginTop: 14, fontSize: 22, letterSpacing: 8, color: '#8fa3e8'}}>LIVE PREVIEW · REMOTION</div>
			</div>
		</AbsoluteFill>
	);
};

export const RemotionRoot: React.FC = () => {
	return (
		<>
			<Composition
				id="AudioStory"
				component={AudioStory}
				width={1080}
				height={1080}
				fps={30}
				schema={audioStorySchema}
				defaultProps={{
					title: '声波手记',
					subtitle: 'SONIC NOTES · AUDIO-DRIVEN VIDEO',
					episode: 'EP.01',
					accentColor: '#ffb347',
					audioFile: staticFile('audio.wav'),
					captionsFile: staticFile('captions.json'),
					captions: null,
				}}
				calculateMetadata={async ({props}) => {
					const [captions, durationInSeconds] = await Promise.all([
						fetchCaptions(props.captionsFile),
						getAudioDurationInSeconds(props.audioFile),
					]);
					return {
						durationInFrames: Math.floor(durationInSeconds * 30),
						props: {...props, captions},
					};
				}}
			/>
			<Composition id="NeonRain" component={NeonRain} durationInFrames={450} fps={30} width={1920} height={1080} />
			<Composition id="LightAnatomy" component={LightAnatomy} durationInFrames={450} fps={30} width={1920} height={1080} />
			<Composition id="TravelDay" component={TravelDay} durationInFrames={450} fps={30} width={1920} height={1080} />
			<Composition id="SanhaojiTrailer" component={SanhaojiTrailer} durationInFrames={450} fps={30} width={1920} height={1080} />
			<Composition id="HelloRemotion" component={HelloRemotion} durationInFrames={180} fps={30} width={1920} height={1080} />
			<Composition id="PulseGrid" component={PulseGrid} durationInFrames={150} fps={30} width={1080} height={1080} />
		</>
	);
};
